import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import { Button } from "../components/Button.jsx";

const TUTORIAL_TOOLTIP_WIDTH = "min(420px, calc(100vw - 24px))";

export function TutorialJoyrideTooltip({
  backProps,
  index,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
}) {
  const theme = useTheme();
  const {
    content,
    hideBackButton,
    hideFooter,
    isTransitioning,
    onBackClick,
    onPrimaryClick,
    showSkipButton,
    title,
  } = step;

  return (
    <Box
      {...tooltipProps}
      // Marcador común para que componentes globales detecten que hay un tutorial activo. (desde FloatingCopilotButton desactivarlo para evitar inconsistencias en UI)
      data-tutorial-active="true"
      data-tutorial-surface="joyride"
      sx={{
        width: TUTORIAL_TOOLTIP_WIDTH,
        maxWidth: TUTORIAL_TOOLTIP_WIDTH,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        pointerEvents: "none",
        opacity: isTransitioning ? 0 : 1,
        visibility: isTransitioning ? "hidden" : "visible",
        transition: "opacity 0.12s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 24px 12px 24px",
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: 1.3,
            textAlign: "left",
            flex: 1,
            minWidth: 0,
          }}
        >
          {title}
        </Typography>

        {typeof step.globalStep === "number" && (
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: "flex-start",
              px: 1.5,
              py: 0.75,
              borderRadius: "10px",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.text.primary,
              fontSize: "0.82rem",
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {step.globalStep} / {step.globalTotal}
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: "24px", textAlign: "left" }}>
        <Box
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "0.95rem",
            lineHeight: 1.6,
          }}
        >
          {content}
        </Box>
      </Box>

      {!hideFooter && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              p: "16px 24px",
              flexWrap: "wrap",
              pointerEvents: "auto",
            }}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={skipProps.onClick}
              disabled={Boolean(isTransitioning)}
            >
              Finalizar tutorial
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {showSkipButton && (
                <Button variant="ghost" size="sm" onClick={skipProps.onClick}>
                  {skipProps.title}
                </Button>
              )}

              {!hideBackButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBackClick || backProps.onClick}
                  disabled={Boolean(isTransitioning) || (index === 0 && !onBackClick)}
                >
                  Anterior
                </Button>
              )}

              <Button
                size="sm"
                onClick={onPrimaryClick || primaryProps.onClick}
                disabled={Boolean(isTransitioning)}
              >
                {primaryProps.title}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export const getTutorialJoyrideStyles = (theme, zIndex = 10000) => ({
  options: {
    primaryColor: theme.palette.primary.main,
    zIndex,
    overlayColor: alpha(theme.palette.common.black, 0.38),
  },
});

const VIEWPORT_PADDING = 12;

const clamp = (value, min, max) => Math.max(min, Math.min(value, Math.max(min, max)));

// Popper v1 modifier, runs after preventOverflow/keepTogether/flip. Joyride forces
// boundariesElement="window" when the target has its own scroll parent, and keepTogether
// pushes the tooltip off-screen when the target is taller than the viewport (tables, panels).
// Last resort: clamp into the viewport, even if that overlaps the target.
function keepInViewport(data) {
  const { popper } = data.offsets;
  let originTop = 0;
  let originLeft = 0;
  if (popper.position !== "fixed") {
    const parent = data.instance.popper.offsetParent || document.documentElement;
    const rect = parent.getBoundingClientRect();
    originTop = rect.top;
    originLeft = rect.left;
  }

  const viewportTop = -originTop + VIEWPORT_PADDING;
  const viewportLeft = -originLeft + VIEWPORT_PADDING;
  const nextTop = clamp(popper.top, viewportTop, -originTop + window.innerHeight - popper.height - VIEWPORT_PADDING);
  const nextLeft = clamp(popper.left, viewportLeft, -originLeft + window.innerWidth - popper.width - VIEWPORT_PADDING);

  // Keep the arrow pointing at the target on the cross axis.
  const arrow = data.offsets.arrow;
  const vertical = data.placement.startsWith("top") || data.placement.startsWith("bottom");
  if (arrow) {
    if (vertical && typeof arrow.left === "number") arrow.left -= nextLeft - popper.left;
    if (!vertical && typeof arrow.top === "number") arrow.top -= nextTop - popper.top;
  }

  popper.top = nextTop;
  popper.left = nextLeft;
  return data;
}

export const TUTORIAL_FLOATER_PROPS = {
  options: {
    preventOverflow: { boundariesElement: "viewport", padding: VIEWPORT_PADDING },
    flip: { boundariesElement: "viewport" },
    keepInViewport: { enabled: true, order: 840, fn: keepInViewport },
  },
};
